import { Box, Card, CardContent, CardHeader, Checkbox, FormControlLabel, Grid, LinearProgress, Theme, Typography, createStyles, withStyles } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import GetAppIcon from '@material-ui/icons/GetApp';
import { debounce } from 'lodash';
import React, { Fragment } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import FirebaseContext from '../../providers/Firebase/context';
import { ApiBackend } from '../../providers/apibackend';
import SearchFilter, { Filters, IGenericSelectFilterItem } from '../Common/SearchFilter/SearchFilter';
import { vasaloppetDateGetterFormatter } from '../../utilities/date';
import { AccountingAccountItem } from '../../model/IGetAccountingAccountsResponse';
import OrderTransactionOverViewItem from './OrderTransactionOverViewItem';
import { searchFilterToExportExcelFilter } from '../Common/SearchFilter/utils';
import { IExportExcelRequest } from '../../model/IExportExcelRequest';
import { PointOfSale } from '../../model/PointOfSale';

interface ISearchFilter {
    fromDate: string; // Order
    toDate: string; // Order
    product?: string; // Product 
    account?: string;
    paymentChannel?: string;

}

interface State {
    loading: boolean;
    groupBy: "account" | "product";
    accounts: AccountingAccountItem[];
    searchFilter: ISearchFilter;
    paymentChannels: PointOfSale[];
}

class OrderTransactionOverview extends React.Component<RouteComponentProps, State> {
    static contextType = FirebaseContext;

    private readonly api: ApiBackend;

    constructor(props: RouteComponentProps) {
        super(props);

        this.api = new ApiBackend();

        const fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - 365);

        const toDate = new Date(2030, 11, 31);

        this.state = {
            loading: true,
            groupBy: "account",
            accounts: [],
            paymentChannels: [{"name" : 'Swedbank Pay', id: null, account:null}],
            searchFilter: {
                fromDate: vasaloppetDateGetterFormatter(fromDate),
                toDate: vasaloppetDateGetterFormatter(toDate),
                paymentChannel: null,
                account: null
            }
        };
    }

    componentDidMount() {
        this.init();

    }

    refresh = () => {
        this.setState({ loading: true });
        this.performSearch();
    }

    render() {
        const { classes } = this.props as any;
        const { searchFilter, accounts } = this.state;

        const defaultBoxStyle = {
            display: "flex",
            flexDirection: "row",
        };

        return <>
            <SearchFilter
                key="sf"
                id={"ordertransactions-filter"}
                filters={{
                    "fromDate": {
                        id: "filter-fromdate",
                        type: "Date",
                        label: "Fråndatum",
                        size: 3,
                        defaultValue: searchFilter.fromDate
                    },
                    "toDate": {
                        id: "filter-todate",
                        type: "Date",
                        label: "Tilldatum",
                        size: 3,
                        defaultValue: searchFilter.toDate
                    },
                    "paymentChannel": {
                        id: "filter-paymenchannel",
                        type: "GenericSelect",
                        label: "Betalkanal",
                        size: 3,
                        defaultValue: searchFilter.paymentChannel,
                        itemDefinition: {
                            itemValueType: "string",
                            items: this.state.paymentChannels.map((ch) => {
                                return {"name" : ch.name, "value": ch.name}
                            })
                        }
                    },
                    "account": {
                        id: "filter-account",
                        type: "GenericSelect",
                        label: "Konto",
                        size: 3,
                        defaultValue: searchFilter.account,
                        clearable: true,
                        itemDefinition: {
                            itemValueType: "number",
                            items: accounts.map((ch) => {
                                return {"name" : ch.account +"", "value": ch.accountid}
                            })
                        }
                    }
                }}
                persist={true}
                onInit={async (filter: Filters<ISearchFilter>) => {
                    await this.handleSearchFilterChange(filter as unknown as ISearchFilter);
                }}
                onChange={async (filter: Filters<ISearchFilter>) => {
                    console.log(filter);
                    await this.handleSearchFilterChange(filter as unknown as ISearchFilter);
                }}
            />
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FormControlLabel label={"Gruppera per konto"} control={<Checkbox checked={this.state.groupBy == "account"} onChange={(ev) => {
                        if (ev.target.checked) {
                            this.setState({ groupBy: 'account' }, () => { this.performSearch(); });
                        }
                    }} />} />
                    <FormControlLabel label={"Gruppera per produkt"} control={<Checkbox checked={this.state.groupBy == "product"} onChange={(ev) => {
                        if (ev.target.checked) {
                            this.setState({ groupBy: 'product' }, () => { this.performSearch(); });
                        }
                    }} />} />
                </Grid>
            </Grid>
            <Grid container className={classes.root} spacing={2}>
                <Grid item xs={12}>
                    <Card>
                        <CardHeader className={classes.cardHeader}
                            title={
                                <Fragment>
                                    <Typography variant="h5" style={{ display: 'inline' }}>Kontotransaktioner </Typography>
                                    <RefreshIcon style={{ display: 'inline', verticalAlign: 'middle', cursor: 'pointer' }} onClick={this.refresh} />
                                </Fragment>
                            }
                            action={
                                <Box {...defaultBoxStyle} style={{ gap: "16px", marginLeft: "auto" }}>
                                    <div style={{ marginTop: 12, marginRight: 12 }}>
                                        <GetAppIcon onClick={this.export} />
                                    </div>
                                </Box>
                            }
                        />
                        <CardContent>
                            {this.state.loading &&
                                <LinearProgress color="secondary" />
                            }
                        </CardContent>

                        <div style={{ padding: 3 }}>

                            {this.state && this.state.accounts && this.state.accounts.map(x => {
                                return <OrderTransactionOverViewItem account={x} key={x.account} fromDateFilter={this.state.searchFilter.fromDate} toDateFilter={this.state.searchFilter.toDate} paymentChannelFilter={this.state.searchFilter.paymentChannel} />
                            })}
                        </div>

                    </Card>
                </Grid>
            </Grid>
        </>;
    }

    private handleSearchFilterChange = async (searchFilter: ISearchFilter): Promise<void> => {
        if (this.state.searchFilter.paymentChannel !== searchFilter.paymentChannel)
        {
            searchFilter.account = null;
        }
        this.setState({ searchFilter: searchFilter, loading: true }, async () => {
            await this.performSearch();
        });
    };

    private export = async () => {
        const { searchFilter } = this.state;

        const filter = searchFilterToExportExcelFilter<ISearchFilter>(searchFilter);

        const request: IExportExcelRequest = {
            entityType: "AccountingAccounts",
            filter: filter
        };
        try {
            this.setState({ loading: true });
            await this.api.exportExcel(request);
        } finally {
            this.setState({ loading: false });
        }
    };

    private init = () => {
        this.api.getAccountingAccounts(this.state.searchFilter.fromDate, this.state.searchFilter.toDate, this.state.groupBy, null,null).then((accounts) => {       
            
            accounts.sort((x,y) => { return x.account < y.account ? -1 : 1});
            accounts.splice(0,0,{account:"Alla",accountid:"all"} as AccountingAccountItem);
            this.setState({
            accounts: accounts, loading: false
            });
        });

        this.api.listPointOfSales().then((channels) => {
            channels.sort((x,u) => {return x.name < u.name ? -1 : 1;});
            channels.splice(0,0,{"name" : "Swedbank Pay", id:'Swedbank Pay', account:null});
            channels.splice(0,0,{"name" : "Alla", id:'all', account:undefined});
            this.setState({paymentChannels: channels});
        })
    };

    private performSearch = debounce(async () => {
        this.setState({ loading: true });
        const accounts = await this.api.getAccountingAccounts(this.state.searchFilter.fromDate, this.state.searchFilter.toDate, this.state.groupBy, this.state.searchFilter.paymentChannel != "Alla" ? this.state.searchFilter.paymentChannel : null, this.state.searchFilter.account == "all" ? null : this.state.searchFilter.account);
        accounts.sort((x,y) => x.account < y.account ? -1 : 1);
        this.setState({
            accounts: accounts, loading: false
        });
    }, 500);
}

const useStyles = ({ palette, spacing }: Theme) => createStyles({
    cardHeader: {
        background: palette.secondary.main,
        color: palette.secondary.contrastText,
        padding: 3
    },
    photo: {
        height: '30px',
        verticalAlign: 'middle',
        borderRadius: '10px'
    },
    root: {

    },
    form: {
        '& > *': {
            margin: spacing(1),
            width: '25ch',
        },
        '& label.Mui-focused': {
            color: palette.secondary.main,
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: palette.secondary.main,
        },
    }
});

export default withRouter(withStyles(useStyles)(OrderTransactionOverview));
